import { memo, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { HEX_TILE_SIDES } from '@/game/constants';
import type { HexTileMapKey } from '@/game/hexmap';
import {
  axialHexDistance,
  axialToWorld,
  getHexTileMapKey,
  hexesWithinRadius,
  TILE_COLORS,
} from '@/game/hexmap';
import config from '@/game/hexmap/config';
import { exploredColor } from '@/game/hexmap/view/tiles/sharedMaterials';
import type { TileVisibility } from '@/game/sim';

/**
 * Decorative ocean beyond the playable map: rings of water hexes from just
 * outside the map edge out to `oceanRadius`, fading into the background so
 * the world has no visible edge. Fogged like map tiles (scouts on the coast
 * reveal it), but not raycastable, so it never takes hover or clicks. One
 * InstancedMesh, recoloured only when vision changes.
 */

const { radius, waterThickness } = config.hexMap.tile;
const geometry = new THREE.CylinderGeometry(radius, radius, waterThickness, HEX_TILE_SIDES);
geometry.translate(0, waterThickness / 2, 0);
const material = new THREE.MeshStandardMaterial({ color: 'white' });
// Same colours as map water once explored; unexplored cells aren't drawn,
// so the background (and the clouds over it) show through.
const VISIBILITY_COLORS: Record<Exclude<TileVisibility, 'unexplored'>, THREE.Color> = {
  visible: new THREE.Color(TILE_COLORS.water),
  explored: exploredColor(TILE_COLORS.water),
};
/** Fraction of the ocean band (from the map edge) before the fade starts. */
const FADE_START = 0.15;
const noRaycast = () => null;
const ORIGIN = { q: 0, r: 0 };

interface OceanSurroundProps {
  mapRadius: number;
  oceanRadius: number;
  backgroundColor: string;
  /** By sight; a new function whenever the fog changes, which recolours the ocean. */
  getTileVisibility: (key: HexTileMapKey) => TileVisibility;
}

export const OceanSurround = memo(function OceanSurround({
  mapRadius,
  oceanRadius,
  backgroundColor,
  getTileVisibility,
}: OceanSurroundProps) {
  const cells = useMemo(
    () =>
      hexesWithinRadius(ORIGIN, oceanRadius)
        .filter((c) => axialHexDistance(ORIGIN, c) > mapRadius)
        .map((c) => ({ coord: c, key: getHexTileMapKey(c) })),
    [mapRadius, oceanRadius]
  );
  const meshRef = useRef<THREE.InstancedMesh>(null);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const far = new THREE.Color(backgroundColor);
    const color = new THREE.Color();
    const dummy = new THREE.Object3D();
    cells.forEach(({ coord, key }, i) => {
      const visibility = getTileVisibility(key);
      const world = axialToWorld(coord, radius);
      dummy.position.set(world.x, 0, world.z);
      dummy.scale.setScalar(visibility === 'unexplored' ? 0 : 1);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      if (visibility === 'unexplored') return;
      const t = (axialHexDistance(ORIGIN, coord) - mapRadius) / (oceanRadius - mapRadius);
      const fade = THREE.MathUtils.smoothstep(t, FADE_START, 1);
      mesh.setColorAt(i, color.copy(VISIBILITY_COLORS[visibility]).lerp(far, fade));
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [cells, backgroundColor, mapRadius, oceanRadius, getTileVisibility]);

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material, cells.length]}
      frustumCulled={false}
      raycast={noRaycast}
    />
  );
});
